/**
 * The social-agent screens' shared chrome — one place for the class strings the dialogs, fields
 * and buttons repeat, so a design change is one edit rather than a grep.
 *
 * Held here rather than pushed onto the shared ui/ components: those are used on screens this
 * design pass has not covered, and silently restyling every input in the product to fix one
 * module is the wrong trade. If the rest of the app is later checked against the same Figma
 * nodes, these move up.
 *
 * Deliberate literals (the theme's scale does not land on the design's numbers):
 *  - `rounded-[12px]`, not `rounded-xl`: --radius is 0.625rem and xl derives as radius + 4px = 14.
 *  - `md:text-xs`, not just `text-xs`: Input/Textarea carry `md:text-sm`, a different key to
 *    tailwind-merge, so an unprefixed `text-xs` loses to it above 768px.
 *  - `text-[13px]` / `text-[11px]`: Body/Small and Micro sizes, neither on the font scale.
 * Colours are theme tokens throughout.
 */

/** Secondary/primary pill button as the design system draws it: 32px, full radius, 13px regular. */
export const PILL_BUTTON = 'h-8 rounded-full text-[13px] font-normal';

/** Dialog shell: rounded-[20px] (not the shared dialog's 24), no padding — sections bring their own. */
export const DIALOG_SHELL = 'gap-0 p-0 rounded-[20px]';

/** Dialog heading — Body/Semibold in text/primary. */
export const DIALOG_TITLE = 'text-sm font-semibold leading-5 text-ink';

/** Caption/Medium — 12px, weight 500, gray-900. */
export const FIELD_LABEL = 'text-xs font-medium text-gray-900';

/** Micro/Regular — 11px gray-800. `text-xs` rendered these a step too large. */
export const FIELD_HINT = 'text-[11px] leading-4 text-gray-800';

/**
 * A locked field is FILLED, not faded. Figma 3735:14091 / 3735:14664 grey the surface of a
 * Kim-authored rule's name and condition; the shared `disabled:opacity-50` instead dimmed the
 * border and the text along with it, so the field read as broken rather than as fixed.
 */
export const DISABLED_FIELD = 'disabled:opacity-100 disabled:bg-gray-100 disabled:text-gray-800';

/** The design-system text field — Figma 472:3466 / 2462:14731. */
export const FIELD = `h-10 rounded-[12px] border-gray-500 px-3 py-2 text-xs md:text-xs placeholder:text-xs placeholder:text-gray-800 ${DISABLED_FIELD}`;

/** The design-system textarea. */
export const TEXTAREA = `min-h-20 rounded-2xl border-gray-500 p-3 text-xs md:text-xs placeholder:text-xs placeholder:text-gray-800 ${DISABLED_FIELD}`;

/** A select/multi-select trigger drawn as the same field. */
export const FIELD_TRIGGER = 'rounded-[12px] border-gray-500';

/**
 * The hint, when what it says is consequential rather than merely informative.
 *
 * Used where a default quietly does something bigger than a reader would assume — an unscoped
 * hide or delete rule running on every channel, including ones connected later. Weight and colour
 * only: it is still the field's hint, not an error, and nothing is wrong yet.
 */
export const FIELD_HINT_STRONG = 'font-medium text-amber-700';
